import AbstractShape, {Dimensions, DimensionProperties} from "./AbstractShape";
import Length from "../../../../source/js/shared/models/Geometry/Length";
import DimensionLine from "@/Models/Lines/DimensionLine";
import { makerjs } from "../../../../source/js/shared/rendering/makerjs";
import { cloneObject, IPoint } from "makerjs";

export class Star extends AbstractShape {
	constructor( dimensions?: object, modifiers?: object ) {
		super( 'star', dimensions, modifiers );
	}

	getOuterRadius(): Length {
		return this.cleanedPrimitives.outer_radius;
	}

	getInnerRadius(): Length {
		return this.cleanedPrimitives.inner_radius;
	}

	getLength(): Length {
		return this.cleanedPrimitives.inner_radius;
	}

	getWidth(): Length {
		return Length.from_cm(this.getMeasure()['width']);
	}

	getHeight(): Length {
		return Length.from_cm(this.getMeasure()['height']);
	}

	/**
	 * Gets the number of points.
	 */
	getNumberOfPoints(): number {
		return parseInt( this.cleanedPrimitives.number_of_points );
	}

	getDimensionProperties(): DimensionProperties {
		return { primary: 'inner_radius', secondary: 'outer_radius' };
	}

	getMeasure(): makerjs.IMeasureWithCenter {
		return makerjs.measure.modelExtents(this.model);
	}

	getInnerRadiusPoint(): IPoint {
		const starModel = cloneObject(this.model);
		makerjs.model.scale(starModel, 10); // Model is in cm, but we want to work in mm.
		const chains = makerjs.model.findChains(starModel);
		if(!chains || chains.length === 0) {
			return [];
		}

		const pointsOnChain = makerjs.chain.toKeyPoints(chains[0]);
		return makerjs.point.closest([1, -1], pointsOnChain);
	}

	getDimensionLines(ratio = 1, focusedElement = '', forceRender = false): DimensionLine[] {
		if (!this.model) {
			return [];
		}

		const measure = this.getMeasure();

		if(focusedElement.includes("cutout_measurement") || forceRender) {
			return [

				new DimensionLine( [Length.from_cm(measure['low'][0]).in_mm(), Length.from_cm(measure['low'][1]).in_mm()], [ Length.from_cm(measure['low'][0]).in_mm(), Length.from_cm(measure['high'][1]).in_mm() ], this.renderDimensionLabel( 'height', this.getHeight().in_cm() ), [-40, 0], ratio), // Vertical line

				new DimensionLine( [Length.from_cm(measure['low'][0]).in_mm(), Length.from_cm(measure['low'][1]).in_mm()], [ Length.from_cm(measure['high'][0]).in_mm(), Length.from_cm(measure['low'][1]).in_mm() ], this.renderDimensionLabel( 'width', this.getWidth().in_cm() ), [0, -40], ratio), // Vertical line

				new DimensionLine( [0, 0], [ this.getOuterRadius().in_mm(), 0 ], this.renderDimensionLabel( 'outer_radius' ), [0, 0], ratio, this.getDimensionLineType('measurements-star-outer_radius', focusedElement, forceRender) ), // Outer radius - horizontal line
				new DimensionLine( [0, 0], this.getInnerRadiusPoint(), this.renderDimensionLabel( 'inner_radius' ), [0, 0], ratio, this.getDimensionLineType('measurements-star-inner_radius', focusedElement, forceRender) ), // Inner radius - diagonal line
			];
		}

		return [];
	}

	getDefaultDimensions(): Dimensions {
		return {
			inner_radius: 20,
			outer_radius: 50,
		};
	}

	getDefaultCutoutDimensions(parentShape: AbstractShape): Dimensions {
		const enclosingRectangle = parentShape.getEnclosingRectangle();
		const width = enclosingRectangle.width();

		const outer_radius = width.divide(3).in_cm();

		return {
			inner_radius: outer_radius - ( outer_radius / 2 ),
			outer_radius: outer_radius,
		};
	}

	getDefaultCutoutModifiers(): object {
		return {
			number_of_points: 5,
		};
	}
}
